import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  readConsent,
  saveConsent,
  applyConsent,
  ACCEPT_ALL,
  REJECT_ALL,
  DEFAULT_CONSENT,
} from "@/lib/consent";

export default function CookieBanner({ openSignal = 0 }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setPrefs(existing);
      // reapply saved consent on future visits
      applyConsent(existing);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      const existing = readConsent();
      setPrefs(existing || DEFAULT_CONSENT);
      setManage(true);
      setVisible(true);
    }
  }, [openSignal]);

  const finish = (consent) => {
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
    setManage(false);
  };

  const acceptAll = () => finish(ACCEPT_ALL);
  const rejectAll = () => finish(REJECT_ALL);
  const savePrefs = () => finish({ ...prefs, necessary: true });

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-6 sm:pb-6"
      role="dialog"
      aria-modal="true"
      aria-label={t("cookie.title")}
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-[#0F1B2D]/10 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#0F1B2D]/8 px-5 pt-5 pb-3">
          <div>
            <h3 className="text-[16px] font-semibold text-[#0F1B2D]">
              {manage ? t("cookie.prefTitle") : t("cookie.title")}
            </h3>
            <p className="mt-1 text-[13.5px] text-[#4b5563] leading-relaxed">
              {manage ? t("cookie.prefBody") : t("cookie.body")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="text-[#4b5563] hover:text-[#0F1B2D] flex-shrink-0"
            aria-label={t("nav.close")}
            data-testid="cookie-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {manage ? (
          <div className="px-5 py-4 space-y-3 max-h-[55vh] overflow-y-auto">
            <PrefRow
              title={t("cookie.catNecessary")}
              body={t("cookie.catNecessaryBody")}
              checked={true}
              disabled
            />
            <PrefRow
              title={t("cookie.catAnalytics")}
              body={t("cookie.catAnalyticsBody")}
              checked={prefs.analytics}
              onChange={(v) => setPrefs({ ...prefs, analytics: v })}
              testId="pref-analytics"
            />
            <PrefRow
              title={t("cookie.catAds")}
              body={t("cookie.catAdsBody")}
              checked={prefs.ads}
              onChange={(v) => setPrefs({ ...prefs, ads: v })}
              testId="pref-ads"
            />
            <PrefRow
              title={t("cookie.catPersonalization")}
              body={t("cookie.catPersonalizationBody")}
              checked={prefs.personalization}
              onChange={(v) => setPrefs({ ...prefs, personalization: v })}
              testId="pref-personalization"
            />
          </div>
        ) : null}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 px-5 py-4 bg-[#F7F3EA]">
          {manage ? (
            <>
              <button
                type="button"
                onClick={() => setManage(false)}
                className="inline-flex h-10 items-center justify-center rounded-full px-4 text-[13px] font-medium text-[#0F1B2D] hover:bg-[#0F1B2D]/5"
                data-testid="cookie-back"
              >
                {t("cookie.back")}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="inline-flex h-10 items-center justify-center rounded-full bg-[#0F1B2D] px-5 text-[13px] font-semibold text-[#F7F3EA] hover:bg-[#1c2c46]"
                data-testid="cookie-save"
              >
                {t("cookie.save")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setManage(true)}
                className="inline-flex h-10 items-center justify-center rounded-full border border-[#0F1B2D]/20 px-4 text-[13px] font-medium text-[#0F1B2D] hover:bg-[#0F1B2D]/5"
                data-testid="cookie-manage"
              >
                {t("cookie.manage")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="inline-flex h-10 items-center justify-center rounded-full border border-[#0F1B2D]/20 px-4 text-[13px] font-medium text-[#0F1B2D] hover:bg-[#0F1B2D]/5"
                data-testid="cookie-reject"
              >
                {t("cookie.reject")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex h-10 items-center justify-center rounded-full bg-[#0F1B2D] px-5 text-[13px] font-semibold text-[#F7F3EA] hover:bg-[#1c2c46]"
                data-testid="cookie-accept"
              >
                {t("cookie.accept")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, body, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-[#0F1B2D]/10 bg-white p-3">
      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-[#0F1B2D]">{title}</p>
        <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#4b5563]">{body}</p>
      </div>
      <label className="relative inline-flex flex-shrink-0 h-6 w-11 items-center cursor-pointer">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          data-testid={testId}
        />
        <span
          className={`absolute inset-0 rounded-full transition-colors ${
            disabled
              ? "bg-[#0F1B2D]/60"
              : checked
              ? "bg-[#0F1B2D]"
              : "bg-[#0F1B2D]/15"
          }`}
        />
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </label>
    </div>
  );
}
